/** Shopify Liquid resolves store-local dates and DST before rendering these epochs. */
export function scheduleFromEpoch(start, end) {
  if (![start, end].every((value) => /^\d{1,12}$/.test(String(value)))) return null;
  const from = Number(start) * 1000,
    until = Number(end) * 1000;
  if (from <= 0 || until <= from || until > 8640000000000000) return null;
  return { start: new Date(from).toISOString(), end: new Date(until).toISOString() };
}

/**
 * Advance Shopify's rendered server time by elapsed time only; the visitor's
 * absolute clock is never trusted. performance.now() stops while some devices
 * sleep, so the elapsed wall-clock delta is used when it is larger: a countdown
 * must not keep showing an offer after it ended. A clock set backwards is ignored.
 */
export function createServerClock(epoch, monotonic = () => performance.now(), wall = () => Date.now()) {
  const start = /^\d{1,12}$/.test(String(epoch)) && Number(epoch) > 0 ? Number(epoch) * 1000 : NaN;
  let previousMonotonic = monotonic();
  const wallOrigin = wall();
  let elapsed = 0;
  return () => {
    const currentMonotonic = monotonic();
    // Retain time already observed during sleep, even if the wall clock is corrected later.
    elapsed = Math.max(elapsed + Math.max(0, currentMonotonic - previousMonotonic), wall() - wallOrigin);
    previousMonotonic = currentMonotonic;
    return start + elapsed;
  };
}

/** A shared product must never silently choose or stack overlapping campaigns. */
export function selectActiveCampaign(campaigns, now) {
  const matches = campaigns.filter((campaign) => {
    if (!campaign.enabled || !campaign.applicable || !campaign.schedule) return false;
    const from = Date.parse(campaign.schedule.start);
    const until = Date.parse(campaign.schedule.end);
    return Number.isFinite(from) && Number.isFinite(until) && from <= now && now < until;
  });
  return { campaign: matches.length === 1 ? matches[0] : null, overlap: matches.length > 1 };
}

/** End is exclusive: adjacent campaigns can hand over at the same instant. */
export function campaignOverlap(a, b) {
  if (!a.schedule || !b.schedule) return null;
  const from = Math.max(Date.parse(a.schedule.start), Date.parse(b.schedule.start));
  const until = Math.min(Date.parse(a.schedule.end), Date.parse(b.schedule.end));
  return from < until ? { from, until } : null;
}

export function countdown(ms) {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60]
    .map((value) => String(value).padStart(2, '0'))
    .join(':');
}
